import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
import { useDispatch } from 'react-redux';

import { setWorkflowLoading } from 'common/redux/workflow/workflowSlice';

import featureFlags from '../helpers/featureFlags';

export const WORKFLOW_ACTIONS = Object.freeze({
  UNDEFINED: 0,
  REQUEST_APPROVAL: 1,
  STOP_APPROVAL_REQUEST: 2,
  APPROVE: 3,
  REJECT: 4,
  SHARE: 5,
  SHARE_WITH_THIRD_PARTY: 6,
});

const getLatestVersionFromVersionsArray = (versions) => {
  let value = '';
  if (isNil(versions)) {
    value = '';
  } else if (versions.length === 0) {
    value = 'v1';
  } else {
    const clonedVersions = [...versions];
    const sorted = clonedVersions.sort(
      (a, b) => a.versionNumber > b.versionNumber
    );
    const latest = sorted[sorted.length - 1];
    value = `v${latest.versionNumber}`;
  }
  return value;
};

const WorkflowActionsDictionary = {
  [WORKFLOW_ACTIONS.UNDEFINED]: {
    actionName: undefined,
    buttonText: () => undefined,
    popOverText: undefined,
    icon: '',
  },
  [WORKFLOW_ACTIONS.REQUEST_APPROVAL]: {
    actionName: 'request-approval',
    buttonText: () => 'action.requestApproval',
    popOverText: '',
    icon: 'confirmed',
  },
  [WORKFLOW_ACTIONS.STOP_APPROVAL_REQUEST]: {
    actionName: 'stop-approval-request',
    buttonText: () => 'action.stopApprovalRequest',
    popOverText: undefined,
    icon: 'end-process',
  },
  [WORKFLOW_ACTIONS.APPROVE]: {
    actionName: 'approve',
    buttonText: () => 'action.approve',
    popOverText: undefined,
    icon: 'thumbs-up',
  },
  [WORKFLOW_ACTIONS.REJECT]: {
    actionName: 'reject',
    buttonText: () => 'action.declineApproval',
    popOverText: undefined,
    icon: 'thumbs-down',
  },
  [WORKFLOW_ACTIONS.SHARE_WITH_THIRD_PARTY]: {
    actionName: 'share-with-third-party',
    buttonText: () => 'shareWithThirdParty',
    popOverText: undefined,
    icon: 'view',
  },
  [WORKFLOW_ACTIONS.SHARE]: {
    actionName: 'share',
    buttonText: (version) =>
      featureFlags.isVersioningPhase1Enabled
        ? 'shareWithClient'
        : {
            key: 'shareVersionWithClient',
            params: {
              version,
            },
          },
    popOverText: {
      title: 'shareWithClientTeam',
      message: 'kfa.shareConfirmationMessage',
      confirmButtonText: 'popover.shareConfirm',
      customContent: 'liveLinking',
    },
    icon: 'view',
  },
};

const getWorkflowButtonTextWithVersion = (action, versions) => {
  const version = getLatestVersionFromVersionsArray(versions);
  return WorkflowActionsDictionary[action].buttonText(version);
};

const workflowActionFactory =
  (content, versions, getWorkflowState, setWorkflowState, dispatch, t) =>
  (nextSteps) =>
  (action, statusUpdater) => {
    const actionText = getWorkflowButtonTextWithVersion(action, versions);
    return nextSteps.has(action)
      ? {
          action,
          name: WorkflowActionsDictionary[action].actionName,
          text:
            typeof actionText === 'object'
              ? t(actionText.key, {
                  ...actionText.params,
                })
              : t(actionText),
          confirmation:
            typeof WorkflowActionsDictionary[action].popOverText === 'object'
              ? Object.entries(
                  WorkflowActionsDictionary[action].popOverText
                ).reduce((obj, [key, value]) => {
                  obj[key] = t(value);
                  return obj;
                }, {})
              : undefined,
          update: () => {
            dispatch(setWorkflowLoading(true));
            return statusUpdater().then(() => {
              getWorkflowState()
                .then(setWorkflowState)
                .then(() => {
                  dispatch(setWorkflowLoading(false));
                })
                .catch(() => {
                  dispatch(setWorkflowLoading(false));
                });
            });
          },
          icon: WorkflowActionsDictionary[action].icon,
        }
      : undefined;
  };
export const useApprovalWorkflow = (
  getWorkflow,
  handleClick,
  projectId,
  contentId,
  content,
  versions
) => {
  const [nextSteps, setNextSteps] = useState(new Set([]));
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const setWorkflowState = (workflow) => {
    setNextSteps(new Set((workflow && workflow.nextSteps) || []));
  };

  const contentHasError = content && content.hasError;

  useEffect(() => {
    if (!contentHasError && contentId) {
      getWorkflow(projectId, contentId).then((workflow) => {
        setWorkflowState(workflow);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!contentHasError, contentId, projectId]);

  const createWorkflowAction = workflowActionFactory(
    content,
    versions,
    () => getWorkflow(projectId, content && contentId),
    setWorkflowState,
    dispatch,
    t
  )(nextSteps);

  return Array.from(nextSteps).map((step) =>
    createWorkflowAction(step, () =>
      handleClick(projectId, content && contentId, step)
    )
  );
};

const workflowQueryActionFactory =
  (versions) => (nextSteps, t) => (action, statusUpdater) => {
    const actionText = getWorkflowButtonTextWithVersion(action, versions);
    return nextSteps.has(action)
      ? {
          action,
          name: WorkflowActionsDictionary[action].actionName,
          text:
            typeof actionText === 'object'
              ? t(actionText.key, {
                  ...actionText.params,
                })
              : t(actionText),
          confirmation: WorkflowActionsDictionary[action].popOverText,
          update: () => statusUpdater(),
          icon: WorkflowActionsDictionary[action].icon,
        }
      : undefined;
  };
export const useApprovalWorkflowQuery = (
  getWorkflowQuery,
  onUpdateWorkflowStatus,
  {
    projectId,
    contentId,
    isLoadingContent,
    isLoadingVersions,
    isError,
    versions,
  }
) => {
  // refetch is done by invalidating the value from status update mutation in API service
  const skip =
    isLoadingContent ||
    isLoadingVersions ||
    isError ||
    !contentId ||
    !projectId;
  const { t } = useTranslation();
  const { data = {}, isLoading } = getWorkflowQuery(
    {
      projectId,
      contentId,
    },
    { skip }
  );

  const nextSteps = new Set(data.nextSteps || []);

  if (!data || nextSteps.size === 0 || isLoading) {
    return [];
  }

  const createWorkflowAction = workflowQueryActionFactory(versions)(
    nextSteps,
    t
  );

  return Array.from(nextSteps).map((step) =>
    createWorkflowAction(step, () =>
      onUpdateWorkflowStatus({ projectId, contentId, step })
    )
  );
};
